<script lang="ts">
  import { tick } from 'svelte';
  import { ApiError, type Conversation } from '../lib/api';
  import { app } from '../lib/stores.svelte';
  import Composer from './Composer.svelte';
  import Message from './Message.svelte';
  import TaskPanel from './TaskPanel.svelte';

  let { conversation, rootId, onClose }: { conversation: Conversation; rootId: string; onClose: () => void } = $props();

  let error = $state('');
  let scroller: HTMLDivElement | undefined = $state();
  const thread = $derived(app.threads[rootId]);
  const archived = $derived(!!conversation.archived_at);
  const live = $derived(thread?.replies.filter((r) => !r.deleted).length ?? 0);

  $effect(() => {
    const id = rootId;
    error = '';
    app
      .loadThread(id)
      .then(async () => {
        await tick();
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      })
      .catch((e) => (error = e instanceof ApiError ? e.message : 'Fil introuvable'));
  });

  // New replies come into view.
  $effect.pre(() => {
    void thread?.replies.length;
    tick().then(() => {
      if (scroller) scroller.scrollTop = scroller.scrollHeight;
    });
  });
</script>

{#if thread?.root.kind === 'task' && thread.root.task}
  <TaskPanel {conversation} root={thread.root} replies={thread.replies} {onClose} />
{:else}
<aside class="panel" aria-label="Fil de discussion">
  <header>
    <h2>Fil <span class="where">· {conversation.kind === 'public' || conversation.kind === 'private' ? `# ${conversation.title}` : conversation.title}</span></h2>
    <button type="button" class="close" aria-label="Fermer le fil" onclick={onClose}>
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  </header>
  <div class="scroller" bind:this={scroller}>
    {#if error}
      <p class="error" role="alert">{error}</p>
    {:else if !thread}
      <p class="status">Chargement…</p>
    {:else}
      <Message message={thread.root} inThread {archived} />
      <p class="count">{live === 0 ? 'Aucune réponse' : `${live} réponse${live > 1 ? 's' : ''}`}</p>
      {#each thread.replies as reply (reply.id)}
        <Message message={reply} inThread {archived} />
      {/each}
    {/if}
  </div>
  {#if thread && !archived && !thread.root.deleted}
    {#key rootId}
      <Composer conversationId={conversation.id} rootId={thread.root.id} placeholder="Répondre dans le fil" />
    {/key}
  {/if}
</aside>
{/if}

<style>
  .panel {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg);
    border-left: 1px solid var(--border);
    min-width: 0;
  }
  header {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: var(--header-h);
    padding: 8px 12px 8px 16px;
    border-bottom: 1px solid var(--border);
  }
  h2 {
    flex: 1;
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .where {
    color: var(--muted);
    font-weight: 400;
    font-size: 14px;
  }
  .close {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .close:hover {
    background: var(--bg-hover);
    color: var(--ink);
  }
  .scroller {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px 4px;
  }
  .count {
    margin: 8px 16px;
    font-size: 12px;
    font-weight: 600;
    color: var(--muted);
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .count::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border);
  }
  .status,
  .error {
    margin: 16px;
  }
  .status {
    color: var(--muted);
  }
</style>
